import {
  KeyboardEvent,
  KeyboardEventHandler,
  useCallback,
  useMemo,
  useState,
} from 'react'
import clsx from 'clsx'
import { Stack, styled } from '@mui/material'
import { AquaList as Aqua, registerAquaManager } from '@weblite/aqua'
// @ts-ignore
import hostConf from '$config/frontend'
import {
  editLastMessage,
  loadNextMessagesOfRoom,
  loadPreviousMessagesOfRoom,
  shouldLoadMessages,
  useOpenedRoomId,
  useOpenedRoomPermissions,
} from '$logic/opened-room'
import { useRoomAttribute } from '$logic/room/react-hook'
import { closeRoom } from '$logic/router'
import { isPhoneOrTablet } from '$func/environmentInfo'
import { roomSearchApi } from '$comp/_app/components/chat/room'
import { editorAPI } from '$comp/_app/components/chat/components/footer/components/send-field/components/text-editor'
import { saveRoomLastScroll } from '$logic/room-last-scroll'
import { pinContentApi } from '$comp/_app/components/chat/components/pin/components'
import {
  DateSeparator,
  MessageListEmpty,
  MessageListLoading,
  MessageWrapper,
  ScrollToBottom,
  ScrollToMention,
  UnseenSeparator,
} from './components'
import {
  IDateSeparator,
  MessageListItem,
  OnboardingMessage,
  OnboardingMessageTypes,
  useOpenedRoomMessageList,
  useOpenedRoomOnboardingMessage,
} from './react-hooks'
import { JoinedChannelEvent } from './components/joined-channel-event/joined-channel-event'
import { HallOnboarding } from '$comp/_app/components/chat/components/body/components/messageList/messages/message-bodies/onboarding/hall'
import { PVOnboarding } from '$comp/_app/components/chat/components/body/components/messageList/messages/message-bodies/onboarding/pv'
import { Scroller } from '$common/scroller'
import { ChannelCourseVitrineButton } from '$comp/_app/components/chat/components/body/components/messageList/components/channel-course-vitrine-button/channel-course-vitrine-button'
import {
  useRoomHasCourseVitrine,
  useRoomHasIssue,
} from '$logic/hall/react-hook'
import { ChannelIssueButton } from './components/channel-issue-button/channel-issue-button'
import { COURSE_VITRINE_BUTTON_HEIGHT } from '../../../conf'

export const ROOM_MESSAGE_LIST_AQUA_MANAGER_ID = 'room-message-list'
registerAquaManager(ROOM_MESSAGE_LIST_AQUA_MANAGER_ID)

const Container = styled('div')`
  display: flex;
  flex-direction: column;
  position: relative;
  height: 100%;
  justify-content: flex-end;
  padding: 0;

  &.empty {
    justify-content: center;
  }

  & > ul {
    direction: rtl;
  }
`

const LoadingContainer = styled('div')`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
`

const UP_KEY = 38
const onKeyDown: KeyboardEventHandler<HTMLElement> = e => {
  if (e.key === 'Enter' && !e.shiftKey && !isPhoneOrTablet) {
    e.preventDefault()
    editorAPI.focus()
    return
  }

  if (e.key === 'Escape') {
    e.preventDefault()
    closeRoom()
    return
  }

  if (e.keyCode === UP_KEY && !editorAPI.getText()) {
    e.preventDefault()
    editLastMessage()
    return
  }

  if ((e.ctrlKey || e.metaKey) && e.key === 'f') {
    e.preventDefault()
    roomSearchApi.handleSearch('')
  }
}

export function MessageList({
  toggleSelection,
  vitrineButtonOffset,
}: {
  toggleSelection: (selected: boolean) => (iid: number) => unknown
  vitrineButtonOffset?: number
}) {
  const [loadingNextMessages, setLoadingNextMessages] = useState(false)
  const [loadingPreviousMessages, setLoadingPreviousMessages] = useState(false)

  const [shouldHideScrollButton, setShouldHideScrollButton] = useState(true)
  const {
    loading: messagesLoading,
    messageList,
    lastVisibleMessage,
  } = useOpenedRoomMessageList()

  const {
    onboardingMessage,
    shouldRenderAboveChatList: shouldRenderOnboardingMessage,
  } = useOpenedRoomOnboardingMessage(messageList, messagesLoading)

  const roomId = useOpenedRoomId() || ''
  const roomType = useRoomAttribute('type', roomId)
  const isChannel = roomType === 'CHANNEL'
  const isGroup = roomType === 'GROUP'

  // TODO: check performance issues
  const { openedRoomPermissions, loading: permissionLoading } =
    useOpenedRoomPermissions()

  const { sendMessage, sendMedia, sendWapp } = openedRoomPermissions || {}
  const hasPermissionToSendMessage = !!(sendMessage || sendMedia || sendWapp)

  const courseVitrineEnabled = useRoomHasCourseVitrine(roomId)

  const showVitrineButton = isChannel && hostConf.HAS_CHANNEL_VITRINE

  const showCourseVitrineButton =
    courseVitrineEnabled && hasPermissionToSendMessage

  const showIssueButton = useRoomHasIssue(roomId)

  const issueButtonOffset =
    (showCourseVitrineButton ? COURSE_VITRINE_BUTTON_HEIGHT + 4 : 0) +
    (vitrineButtonOffset || 0)

  const renderOnboardingMessage = useCallback(
    (message: OnboardingMessage, shouldRender = true) => {
      if (!shouldRender) return undefined
      if (message.onboardingType === OnboardingMessageTypes.PV)
        return (
          <PVOnboarding
            withSeparator={'withSeparator' in message && message.withSeparator}
          />
        )
      if (
        message.onboardingType === OnboardingMessageTypes.CHANNEL ||
        message.onboardingType === OnboardingMessageTypes.GROUP
      )
        return (
          <HallOnboarding
            withSeparator={'withSeparator' in message && message.withSeparator}
          />
        )
      return undefined
    },
    [],
  )

  const renderer = useCallback(
    (message: MessageListItem) => {
      const { type, posInBlock, showSender, needVisibility, ...messageRest } =
        message

      if (type === 'UNSEEN_SEPARATOR')
        return (
          <UnseenSeparator key="UNSEEN_SEPARATOR" posInBlock={posInBlock} />
        )

      if (type === 'DATE_SEPARATOR')
        return (
          <DateSeparator
            date={(message as IDateSeparator).date}
            key={(message as IDateSeparator).date}
            posInBlock={posInBlock}
          />
        )

      if (type === 'JOINED_CHANNEL_EVENT')
        return <JoinedChannelEvent posInBlock={posInBlock} />

      if (type === 'ONBOARDING')
        return renderOnboardingMessage(message, shouldRenderOnboardingMessage)

      // WARNING: Server adds iid to the end of message's id; because of this, when message gets sent and is not pending anymore, MessageWrapper gets reMounted.
      //        So it's needed to remove iid from the id. But if a user sends multiple requests to the server to create messages with the same id,
      //        There will be multiple MessageWrapper components with the same key. This issue should be handled in the future

      // NOTE: For event messages sent from server, the id equals to `roomId_iid`; So to avoid duplicate keys,
      //       we use the full id sent from server for key. For other messages, the firstPart (messageId) is used.

      const isWapp = type === 'WAPP'
      const messageId = messageRest.id
      const keyWithoutIid = messageId.split('_')[0]
      const key =
        keyWithoutIid === roomId || !isWapp ? messageId : keyWithoutIid

      return (
        <MessageWrapper
          // @ts-ignore
          key={key}
          // @ts-ignore
          message={{ type, ...messageRest }}
          posInBlock={posInBlock}
          showSender={showSender}
          needVisibility={needVisibility}
          toggleSelection={toggleSelection}
          roomType={roomType}
          hasPermissionToSendMessages={hasPermissionToSendMessage}
        />
      )
    },
    [
      roomType,
      hasPermissionToSendMessage,
      vitrineButtonOffset,
      toggleSelection,
      renderOnboardingMessage,
      shouldRenderOnboardingMessage,
      roomId,
    ],
  )

  const loadPreviousMessages = useCallback(async () => {
    if (loadingPreviousMessages || !(await shouldLoadMessages('PREVIOUS')))
      return

    setLoadingPreviousMessages(true)
    if (!loadingPreviousMessages)
      loadPreviousMessagesOfRoom().finally(() =>
        setLoadingPreviousMessages(false),
      )
  }, [loadingPreviousMessages])

  const loadNextMessages = useCallback(async () => {
    if (loadingNextMessages || !(await shouldLoadMessages('NEXT'))) return

    setLoadingNextMessages(true)
    if (!loadingNextMessages)
      loadNextMessagesOfRoom().finally(() => setLoadingNextMessages(false))
  }, [loadingNextMessages])

  const handleSaveTopElementId = useCallback(
    (id?: string, iid?: number, isAtBottom?: boolean) => {
      if (roomId)
        saveRoomLastScroll(roomId, {
          topViewPortMessageId: id,
          topViewPortMessageIid: iid,
          bottom: Boolean(isAtBottom),
        })
    },
    [roomId],
  )

  const AquaList = useMemo(
    () => (
      <Aqua
        // idKey="_id"
        list={[...onboardingMessage, ...messageList]}
        renderer={renderer}
        onReachTop={loadPreviousMessages}
        onReachBottom={loadNextMessages}
        setShouldHideScrollButton={setShouldHideScrollButton}
        onKeyDown={(a: KeyboardEvent<HTMLElement>) => {
          if (roomId) onKeyDown(a)
        }}
        onChangeTopElement={handleSaveTopElementId}
        lastMessageId={lastVisibleMessage?.id}
        managerId={ROOM_MESSAGE_LIST_AQUA_MANAGER_ID}
        onUserScroll={direction => {
          pinContentApi.onScroll(direction)
        }}
        ScrollerComponent={Scroller}
      />
    ),
    [
      messageList,
      lastVisibleMessage,
      loadPreviousMessages,
      loadNextMessages,
      renderer,
      handleSaveTopElementId,
      roomId,
      onboardingMessage,
    ],
  )

  return (
    <Container className={clsx({ empty: messageList.length === 0 })}>
      {messagesLoading || permissionLoading ? (
        <LoadingContainer>
          <MessageListLoading />
        </LoadingContainer>
      ) : messageList?.length ? (
        <>
          {AquaList}
          {Boolean(loadingPreviousMessages || loadingNextMessages) && (
            <MessageListLoading
              key="INLINE_LOADING"
              isOverlay
              overlayPosition={loadingPreviousMessages ? 'top' : 'bottom'}
              offset={vitrineButtonOffset}
            />
          )}

          {/* {showVitrineButton && (
            <ChannelVitrineButton
              roomId={roomId}
              expanded={shouldHideScrollButton}
              offset={vitrineButtonOffset}
            />
          )} */}

          {(showCourseVitrineButton || showIssueButton) && (
            <div
              style={{
                display: 'flex',
                top: vitrineButtonOffset || 0,
                position: 'absolute',
              }}
            >
              {showCourseVitrineButton && (
                <ChannelCourseVitrineButton
                  roomId={roomId}
                  expanded={shouldHideScrollButton}
                />
              )}

              {showIssueButton && (
                <ChannelIssueButton
                  roomId={roomId}
                  expanded={shouldHideScrollButton}
                />
              )}
            </div>
          )}

          <Stack
            sx={{ position: 'absolute', bottom: '12px', right: '12px' }}
            gap="16px"
            zIndex={1}
          >
            {isGroup && <ScrollToMention />}
            <ScrollToBottom shouldHide={shouldHideScrollButton} />
          </Stack>
        </>
      ) : onboardingMessage.length ? (
        renderOnboardingMessage(onboardingMessage[0])
      ) : (
        <MessageListEmpty />
      )}
    </Container>
  )
}
